<template>
  <div class="seller-details margin-bottom-m">
    <h5 class="margin-bottom-xs">
      {{ sellerInformationTitleText }}
    </h5>
    <Skeleton v-if="isLoading" />
    <Notification v-else-if="sellerDetailsError" severity="error" :closable="false">
      <!-- eslint-disable vue/no-v-html -->
      <div v-html="sellerDetailsError"></div>
    </Notification>
    <div v-else-if="sellerDetails?.length" id="market-place-seller-details-container">
      <div class="grid-x">
        <div
          v-for="sellerDetail in showSellerDetails"
          :key="sellerDetail"
          class="seller-details-container cell small-6 large-3 small-margin-bottom-s medium-margin-bottom-xs padding-right-s"
        >
          <div v-if="sellerDetail.name" v-text="sellerDetail.name"></div>
          <div v-if="sellerDetail.streetAddress" v-text="sellerDetail.streetAddress + ','"></div>
          <div v-if="formatLocation(sellerDetail)" v-text="formatLocation(sellerDetail)"></div>
          <div v-if="sellerDetail.phoneNumber" v-text="sellerDetail.phoneNumber"></div>
          <div v-if="sellerDetail.email" v-text="sellerDetail.email"></div>
        </div>
      </div>
      <div v-if="showViewMoreButton" class="grid-x margin-bottom-xxs">
        <button class="view-more-button link-body" @click="toggleExpanded" v-text="isExpanded ? viewLessText : viewMoreText"></button>
        <button class="link-body" :class="isExpanded ? chevronUp : chevronDown" @click="toggleExpanded"></button>
      </div>
    </div>
    <div class="seller-generic-info">
      <p class="margin-top-xxs">
        {{ sellerInfoText }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMkpSellerDetailsStore } from '@/stores/ppe/useMkpSellerDetailsStore';
import Notification from '@atomic-ui/notification/Notification';
import Skeleton from '@/components/skeleton/MarketPlaceSellerDetails.vue';
import { ORDER_DETAILS } from 'config';

const mkpSellerDetailsStore = useMkpSellerDetailsStore();
const props = defineProps({
  mkpSellerIds: {
    type: Object,
    default() {
      return {};
    },
  },
});
const defaultSellerDisplayNumber = 4;
const isExpanded = ref(false);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const formatLocation = (seller) => {
  return [seller.city, seller.state, seller.zipCode, seller.country].filter(Boolean).join(' ');
};

const sellerDetails = computed(() => mkpSellerDetailsStore.sellerDetails);
const sellerDetailsError = computed(() => mkpSellerDetailsStore.sellerDetailsError);
const isLoading = computed(() => mkpSellerDetailsStore.isLoading);
const showSellerDetails = computed(() => {
  if (isExpanded.value) {
    return sellerDetails.value;
  }
  return sellerDetails.value.slice(0, defaultSellerDisplayNumber);
});
const showViewMoreButton = computed(() => sellerDetails.value?.length > defaultSellerDisplayNumber);

const { sellerInformationTitleText, sellerInfoText, chevronDown, chevronUp, viewLessText, viewMoreText } = ORDER_DETAILS.marketPlaceSellerDetails;

const getMkpSellerDetails = async (mkpSellerIds) => {
  await mkpSellerDetailsStore.GET_SELLER_DETAILS(mkpSellerIds);
};

onMounted(async () => {
  await getMkpSellerDetails(props.mkpSellerIds);
});
</script>

<style lang="scss" scoped>
.seller-details-container {
  word-wrap: break-word;
}
</style>

<style lang="scss" brand="mcom">
@import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-medium-down-black.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-medium-up-black.scss';
</style>

<style lang="scss" brand="bcom">
@import '~@core/csg/src/scss/encodedIcons/bcom/_chevron-small-down.scss';
@import '~@core/csg/src/scss/encodedIcons/bcom/_chevron-small-up.scss';
</style>
